/**
 * CommunityMediaPage — directly-routable community-media (video) player
 * (`/community/media/:id`, where `:id` is the community message document id).
 *
 * Web-first surface with no direct Flutter parity: Flutter's community video
 * pipeline is the legacy public-token mp4 flow and its media tap pushes a
 * content-list pager (`community_screen.dart:719-749`). The web community video
 * pipeline follows the Chat video flow exactly (encrypted-HLS on the Media CDN —
 * story `## Context`), so the tap instead opens this dedicated page, which
 * initializes via `InitializeCommunityVideoUseCase` and plays through the SAME
 * `InlineVideoPlayer` chat uses. The tap-eligibility semantics (ready,
 * non-uploading media only) are preserved by `CommunityPage.handleMediaTap`.
 *
 * The community message arrives ONLY as a route arg (`location.state`, set by
 * `Navigator.navigateToCommunityMedia`). A pasted / shared URL has no args, so
 * the page surfaces an error and NEVER fetches the message by id — the same
 * deliberate anti-deep-link behaviour as `MediaDetailsPage`.
 *
 * Page + feature-hook split (see `presentation.md`): the page owns `useBasePage`,
 * the `screenKey` mint, `useParams`, the `location.state` snapshot, and the auth
 * gate; `useCommunityMediaDetails` owns state + the init side-effect and exposes
 * the `onPageInit` lifecycle bridge + `isFirstLoad`.
 *
 * Thumbnail: unlike chat (E2EE → `EncryptedCachedImage`), community media is NOT
 * E2EE — the poster is the message's absolute `mediaInfo.thumbnail` rendered
 * through the plain `CachedImage` shown until playback starts.
 *
 * App bar: the shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`), which mirrors
 * Flutter's media-viewer bar (`content_list_widget.dart:1112-1147` — back
 * chevron `leading` → `_onBackPress`, `centerTitle: true`, a `.6`-alpha +
 * `BackdropFilter` background). Moving off the hand-rolled row makes four
 * intentional visual changes: the title is now **centered** (Flutter
 * `centerTitle: true`, `base_app_bar.dart:31` — the old left-aligned title was
 * the web-only outlier), it steps from `responsive.font.lg`/`semibold` to
 * `responsive.font.xl`/`bold`, the bar becomes sticky with a blurred
 * 50%-opacity backdrop, and `responsive.safeArea.top` is now honoured. No
 * `leadingQa` is passed, so the back control keeps `BackIconButton`'s default
 * `data-qa-id="back-button"`.
 *
 * Title text is a deliberate web addition — Flutter renders this viewer's bar
 * title-less (`content_list_widget.dart:1126`, `title: Container()`); the web
 * keeps `l10n.community.mediaDetails.title` so a deep-linked viewer names its
 * surface. `DefaultMediaPage` matches Flutter and passes no `title`.
 *
 * The bar renders on the first-load branch too: Flutter builds the same
 * back-arrow bar over its empty/loading body
 * (`content_list_widget.dart:1029-1048`, `_isFetching` →
 * `ExpauseLoadingIndicator` at `:1071-1078`), and `showFirstLoad` also covers
 * the auth-resolution window, so gating the back affordance on it could strand
 * the user on a spinner (`workflow/lessons.md`).
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { InlineVideoPlayer } from '@/presentation/components/video/InlineVideoPlayer';
import { qaAttr } from '@/presentation/utils/qaAttr';

import type { RootState } from '@/presentation/store';

import {
  useCommunityMediaDetails,
  type CommunityMediaDetailsRouteArgs,
} from './hooks/useCommunityMediaDetails';

export const CommunityMediaPage = () => {
  const { id: messageId } = useParams<{ id: string }>();
  const location = useLocation();
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Canonical screen-key wiring — captured ONCE on mount (same pattern as
  // `MediaDetailsPage`). The unique `messageId` in the URL keeps the
  // route-caching mechanism functioning (each opened media has a unique id).
  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  // Snapshot of `location.state` (the route args), taken ONCE at mount. `null`
  // on the pasted / shared-URL path — the hook's anti-deep-link gate fires and
  // the page renders the args-missing error WITHOUT a by-id fetch.
  const routeArgs = useMemo<CommunityMediaDetailsRouteArgs | null>(() => {
    const state = location.state as CommunityMediaDetailsRouteArgs | undefined;
    if (state && state.message) {
      return state;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    videoUrl,
    decryptionKeys,
    status,
    isFirstLoad,
    errorMessage,
    onPageInit,
  } = useCommunityMediaDetails({ messageId, routeArgs });

  // Page-only `useBasePage` — bridges the lifecycle into the hook's memoized
  // `onPageInit`. The hook owns every state mutation `onInit` triggers.
  useBasePage({
    screenKey,
    onInit: onPageInit,
    debug: false,
  });

  // First-load gate (see `presentation.md`): the page spinner is gated on the
  // hook's own `isFirstLoad` (covers the init round-trip) plus the auth gate.
  const showFirstLoad = isFirstLoad || currentUser === null;

  // Post-init (mid-playback) fatal error from the player. The hook owns INIT
  // failures (→ page `status==='error'`); this covers a fatal HLS error AFTER
  // the player mounted, surfacing the same localized video-error message.
  const [videoPlaybackError, setVideoPlaybackError] = useState(false);

  // Leading (back) handler, declared ABOVE the first-load early return so both
  // render branches share one instance and the hook order is identical in every
  // state (same shape as `MediaDetailsPage` / `UserVideosPage`).
  //
  // Deep-link fallback is the Feed, NOT the owning community wall: the wall owner
  // is only knowable from `routeArgs`, and `routeArgs` is non-null exactly when
  // the page was pushed in-app — which is exactly when there IS history to pop
  // and the fallback never runs. The branch that fires (pasted / new-tab URL) is
  // by construction the branch with no args, and `/community/media/:id` carries
  // only the message id. Even with args the owner is not derivable:
  // `CommunityChatMessageEntity` carries `messageUsersInfo` (the message AUTHOR,
  // not the wall owner) and `Navigator.navigateToCommunity` needs a whole
  // `UserEntity`. `CommunityPage` itself falls back to the Feed for the same
  // reason.
  const handleBack = useCallback(() => {
    Navigator.goBackOr(() => Navigator.navigateToFeed());
  }, []);

  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: theme.background,
  } as const;

  if (showFirstLoad) {
    return (
      <div
        {...qaAttr({ id: 'community-media', status: 'loading' })}
        style={{ ...containerStyle, display: 'flex', flexDirection: 'column' }}
      >
        <ExpauseAppBar
          rootQa={{ id: 'community-media-app-bar' }}
          title={l10n.community.mediaDetails.title}
          onLeadingTap={handleBack}
        />

        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ExpauseLoadingIndicator />
        </div>
      </div>
    );
  }

  // Absolute (default-bucket) thumbnail URL off the entity — community media is
  // NOT E2EE, so a plain `CachedImage` renders it (contrast `MediaDetailsPage`'s
  // `EncryptedCachedImage`).
  const thumbnailUrl = routeArgs?.message.mediaInfo?.thumbnail;

  return (
    <div {...qaAttr({ id: 'community-media', status })} style={containerStyle}>
      <ExpauseAppBar
        rootQa={{ id: 'community-media-app-bar' }}
        title={l10n.community.mediaDetails.title}
        onLeadingTap={handleBack}
      />

      {/* Body */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: responsive.spacing.base,
        }}
      >
        {status === 'error' && (
          <span
            style={{
              color: theme.danger,
              fontSize: responsive.font.base,
              textAlign: 'center',
            }}
          >
            {errorMessage}
          </span>
        )}

        {status === 'ready' && videoUrl && decryptionKeys && (
          videoPlaybackError ? (
            <span
              style={{
                color: theme.danger,
                fontSize: responsive.font.base,
                textAlign: 'center',
              }}
            >
              {l10n.community.mediaDetails.videoError}
            </span>
          ) : (
            // Shared, themed inline player (progress bar + native fullscreen) —
            // the SAME encrypted-HLS surface chat uses. The poster is the
            // message's plaintext default-bucket thumbnail (community is not
            // E2EE), rendered through the plain `CachedImage`.
            <InlineVideoPlayer
              screenKey={screenKey}
              videoId={messageId ?? ''}
              videoUrl={videoUrl}
              decryptionKeys={decryptionKeys}
              aspectRatio={routeArgs?.message.mediaInfo?.aspectRatio}
              thumbnail={
                thumbnailUrl ? (
                  <CachedImage
                    src={thumbnailUrl}
                    qaId="community-media-video-thumb"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : undefined
              }
              onError={() => setVideoPlaybackError(true)}
            />
          )
        )}
      </div>
    </div>
  );
};
